import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import cn from 'classnames';
import MissingSomethingModalButton from '@/creditflowresearch/ui/common/components/MissingSomethingModalButton';
import { useScorecardContext } from '@/creditflowresearch/application/League/Scorecard/ScorecardContext';
import { usePageFiltersContext } from '@/creditflowresearch/application/PageFilters/PageFiltersContext';
import CheckUserAccess from '@/creditflowresearch/ui/common/components/CheckUserAccess';
import { LeagueTablesNoAccessComponent } from '@/creditflowresearch/ui/components/league/LeagueTablePage/LeagueTablesNoAccess';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import ScorecardHeader from '@/creditflowresearch/ui/components/league/Scorecard/components/ScorecardHeader';
import ScorecardContent from '@/creditflowresearch/ui/components/league/Scorecard/components/ScorecardContent';
import { useScorecardSeo } from '@/creditflowresearch/ui/components/league/Scorecard/hooks/useScorecardSeo';
import { useScorecardBreadcrumbs } from '@/creditflowresearch/ui/components/league/Scorecard/hooks/useScorecardBreadcrumbs';
import { useCompanyErrorRedirect } from '@/creditflowresearch/ui/components/league/Scorecard/hooks/useCompanyErrorRedirect';
import { useScrollToDeals } from '@/creditflowresearch/ui/components/league/Scorecard/hooks/useScrollToDeals';
import { useSyncProductRegionFilters } from '@/creditflowresearch/ui/components/league/Scorecard/hooks/useSyncProductRegionFilters';
import { useFetchStandings } from '@/creditflowresearch/ui/components/league/Scorecard/hooks/useFetchStandings';
import { UserPlanType } from '@creditflowresearch/user-plan/UserPlan';

import spacesStyles from '@creditflowresearch/uilib/styles/helpers/spaces.module.scss';

const Scorecard = () => {
  const {
    includedRegionIds,
    includedProductIds,
    checkAccessByUserRegions,
    checkAccessByUserProducts,
  } = usePageFiltersContext();
  const {
    query: { products, regions, leagueType },
  } = useRouter();
  const urlSearch = useMemo(() => ({ products, regions }), [products, regions]);

  const {
    state,
    resetScorecardTableStates,
    getPageData,
    initProductRegionFilters,
  } = useScorecardContext();
  const { checkAccessByUserPlan } = useUserPlanContext();

  const {
    isFetching,
    initialized,
    selectedPeriod,
    company: { data: company, error: errorLoadCompany },
  } = state;

  const [initialFiltersSnapshot, setInitialFiltersSnapshot] = useState({});
  const dealsAnchorRef = useRef<HTMLElement | null>(null);

  const accessChecks = useMemo(() => ([
    checkAccessByUserPlan(UserPlanType.PREMIUM),
    checkAccessByUserRegions(),
    checkAccessByUserProducts(),
  ]), [checkAccessByUserPlan, checkAccessByUserRegions, checkAccessByUserProducts]);
  const hasAccess = accessChecks.every(Boolean);

  useCompanyErrorRedirect(errorLoadCompany);
  useScrollToDeals(selectedPeriod, dealsAnchorRef as React.RefObject<HTMLElement>);
  useScorecardBreadcrumbs({
    companyName: company?.name,
    leagueType,
    initialProductsRegionsFilters: initialFiltersSnapshot,
  });
  const { title, description } = useScorecardSeo({ companyName: company?.name });

  const handleProductRegionChange = useCallback(() => {
    resetScorecardTableStates();
  }, [resetScorecardTableStates]);

  useSyncProductRegionFilters({
    isStandingInitialized: initialized,
    search: urlSearch,
    initialFiltersSnapshot,
    setInitialFiltersSnapshot,
    initProductRegionFilters,
    onProductRegionChange: handleProductRegionChange,
  });

  useFetchStandings({
    hasAccess,
    leagueType,
    productIds: includedProductIds,
    regionIds: includedRegionIds,
    products,
    regions,
    getPageData,
  });

  const assignDealsAnchorRef = useCallback((el: HTMLElement) => {
    (dealsAnchorRef as React.MutableRefObject<HTMLElement | null>).current = el;
  }, []);

  return (
    <>
      <ScorecardHeader
        title={ title }
        description={ description }
        companyName={ company?.name }
        checks={ accessChecks }
        isPageLoading={ isFetching }
      />
      <CheckUserAccess conditions={ [{ checks: accessChecks, fallback: LeagueTablesNoAccessComponent }] }>
        <ScorecardContent
          isScorecardTableFetching={ isFetching }
          isStandingInitialized={ initialized }
          refCallback={ assignDealsAnchorRef }
        />
      </CheckUserAccess>
      <MissingSomethingModalButton className={ cn(spacesStyles.ptl, spacesStyles.pbxl) } />
    </>
  );
};

export default Scorecard;
